// DataService.ts
//import shieldBashImage from "../../../ChampionPage/RunePage/Rune/ShieldBash.png";
import customSpellImages from "./DataCustomSpellImages";
import { ItemDetails } from "./GameSimulator/GameContainer2/GameGrid/ChampionCard/SettingsPanel/ItemShopPanel2";
import { RUNE_TREES } from "./BuildOverview/runeConfig";
import SHARD_ICON_PATHS, { runePathMappings } from "./BuildOverview/RuneDisplay/Runes/runePathMappings";
import { summonerSpellNameToKey } from "./mappings/summonerSpellMappings";

export interface ChampionBuildOpGG {
  name: string;
  scraped_stats: {
    win_rate: string;
    pick_rate: string;
    ban_rate: string;
  };
  runes: {
    primary: string[];
    secondary: string[];
  };
  shards: string[];
  starter_items: string[];
  item_build_wr: string[];
  lanes: string[];
  skill_order: string[];
}

// Additionally, define an interface for the response format when fetching all champions' data
interface ChampionsBuildData {
  [key: string]: ChampionBuildOpGG;
}

// Interface for champion defaults from championComboBox.json
export interface ChampionDefaultSetup {
  level: number;
  items: string[];
  runes: {
    primary: string[];
    secondary: string[];
    shards: string[];
  };
  skillOrder: string[];
}

export interface ChampionDefaultsMap {
  [championName: string]: {
    [level: string]: ChampionDefaultSetup;
  };
}

export interface ChampionHeaderData {
  name: string;
  title: string;
  image: {
    full: string;
  };
  stats: {
    hp: number;
    // ... include other stats fields here
  };
  spells: SpellImage[];
  passive: {
    name: string;
    description: string;
    image: {
      full: string;
    };
    imagePath: string; // This field will be added after fetching
  };
}

export interface SpellImage {
  id: string;
  name: string;
  description: string;
  image: {
    full: string;
    sprite: string;
    group: string;
    x: number;
    y: number;
    w: number;
    h: number;
  };
  imagePath: string; // This field will be added after fetching
}

export interface ShardImagePaths {
  [key: string]: string;
}

class DataService {
  private latestVersion: string | null = null;
  baseUrl: string = "https://ddragon.leagueoflegends.com/cdn";
  private championDefaultsCache: ChampionDefaultsMap | null = null;
  
  // Static version cache for use without instance
  private static staticLatestVersion: string | null = null;
  
  // Static method to fetch latest version (can be used without instance)
  static async fetchLatestVersion(): Promise<string> {
    if (DataService.staticLatestVersion) return DataService.staticLatestVersion;

    const response = await fetch(
      "https://ddragon.leagueoflegends.com/api/versions.json"
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const versions = (await response.json()) as string[];
    DataService.staticLatestVersion = versions[0];
    return DataService.staticLatestVersion;
  }

  //Backend API functions

  // Fetch all champion defaults from championComboBox.json
  async fetchAllChampionDefaults(): Promise<ChampionDefaultsMap> {
    if (this.championDefaultsCache) {
      return this.championDefaultsCache;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/champion-defaults`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.championDefaultsCache = data;
      return data;
    } catch (error) {
      console.error('Failed to fetch champion defaults:', error);
      throw error;
    }
  }

  // Get default setup for a specific champion at a specific level
  async getChampionDefaultSetup(championName: string, level = 11): Promise<ChampionDefaultSetup | null> {
    try {
      const allDefaults = await this.fetchAllChampionDefaults();
      
      if (!allDefaults[championName]) {
        console.warn(`No defaults found for champion ${championName}`);
        return null;
      }
      
      // Find the closest level that doesn't exceed the requested level
      const availableLevels = Object.keys(allDefaults[championName])
        .map(l => parseInt(l))
        .sort((a, b) => a - b);
      
      const closestLevel = availableLevels
        .filter(l => l <= level)
        .pop() || availableLevels[0];
      
      return allDefaults[championName][closestLevel.toString()];
    } catch (error) {
      console.error(`Error fetching default setup for ${championName}:`, error);
      return null;
    }
  }

  // Method to fetch build data for a specific champion
  async fetchChampionBuildData(
    championName: string
  ): Promise<ChampionBuildOpGG> {
    const response = await fetch(
              `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/champion/${championName}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const championData = (await response.json()) as ChampionBuildOpGG;
    return championData;
  }
  // Method to fetch build data for all champions
  async fetchAllChampionsData(): Promise<ChampionsBuildData> {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/champions`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const allChampionsData = (await response.json()) as ChampionsBuildData;
    return allChampionsData;
  }

  async fetchAllItemsData(): Promise<ItemDetails[]> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/items`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = (await response.json()) as Record<string, Omit<ItemDetails, "id">>;

        const itemDetails: ItemDetails[] = Object.entries(data).map(
            ([id, details]) => ({
                ...details,
                id,
            })
        );

     //   console.log("Fetched all items data:", itemDetails);

        return itemDetails;
    } catch (error) {
        console.error("Error in DataService:", error);
        throw error;
    }
}


  async fetchDefaultItems(championName: string) {
    const response = await fetch(
              `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/items/${championName}`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch default items");
    }
    return response.json();
  }

  async fetchCombos(championName: string) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/combos/${championName}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const combos = await response.json();
      return combos;
    } catch (error) {
      console.error("Failed to fetch combos data:", error);
      throw error;
    }
  }

  // Fetch combo data for a matchup (both directions)
  async fetchMatchupCombos(mainChampion: any, opponent: any) {
    try {
      console.log(`[DataService] Fetching matchup combos for ${mainChampion.name} vs ${opponent.name}`);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/combos/matchup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mainChampion,
          opponent
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`[DataService] Successfully fetched matchup combos:`, data);
      return data;
    } catch (error) {
      console.error('[DataService] Error fetching matchup combos:', error);
      throw error;
    }
  }

  // Fetch combo data for a static matchup using champion IDs
  async fetchStaticMatchupCombos(championId: string, targetChampionId: string, forceResimulate: boolean = false) {
    try {
      console.log(`[DataService] Fetching static matchup combos for ${championId} vs ${targetChampionId}${forceResimulate ? ' (force resimulate)' : ''}`);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static/combos/matchup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          championId,
          targetChampionId,
          forceResimulate
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`[DataService] Successfully fetched static matchup combos:`, data);
      return data;
    } catch (error) {
      console.error('[DataService] Error fetching static matchup combos:', error);
      throw error;
    }
  }

  async fetchChampionHeaderData(
    championName: string
  ): Promise<ChampionHeaderData> {

    if (championName === "DummyChampion") {
      // Provide minimal "fake" data
      const dummySpellImageUrl =
        "https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/assets/characters/tft_trainingdummy/hud/icons2d/tft_trainingdummy_donothing.png";

      // Example spells array with 2–4 placeholders (adjust to your needs)
      const dummySpells: SpellImage[] = [
        {
          id: "DummyQ",
          name: "No Q",
          description: "This is not a real Q spell.",
          image: {
            full: "dummyQ.png",
            sprite: "dummyQ.png",
            group: "spell",
            x: 0,
            y: 0,
            w: 48,
            h: 48
          },
          imagePath: dummySpellImageUrl
        },
        {
          id: "DummyW",
          name: "No W",
          description: "This is not a real W spell.",
          image: {
            full: "dummyW.png",
            sprite: "dummyW.png",
            group: "spell",
            x: 0,
            y: 0,
            w: 48,
            h: 48
          },
          imagePath: dummySpellImageUrl
        },
        {
          id: "DummyE",
          name: "No E",
          description: "This is not a real E spell.",
          image: {
            full: "dummyE.png",
            sprite: "dummyE.png",
            group: "spell",
            x: 0,
            y: 0,
            w: 48,
            h: 48
          },
          imagePath: dummySpellImageUrl
        },
        {
          id: "DummyR",
          name: "No R",
          description: "This is not a real R spell.",
          image: {
            full: "dummyR.png",
            sprite: "dummyR.png",
            group: "spell",
            x: 0,
            y: 0,
            w: 48,
            h: 48
          },
          imagePath: dummySpellImageUrl
        }
      ];

      // Return a minimal ChampionHeaderData structure
      return {
        name: "Practice Dummy",
        title: "Training Dummy",
        image: {
          full: dummySpellImageUrl
        },
        stats: {
          hp: 1000 // Provide any stats you need
        },
        spells: dummySpells,
        passive: {
          name: "No Passive",
          description: "This dummy does nothing.",
          image: {
            full: "dummy_passive.png"
          },
          imagePath: dummySpellImageUrl
        }
      };
    }
    const version = await this.fetchLatestVersion();
    
    // Handle champion names that are already formatted as IDs (e.g., "JarvanIV")
    // or names with spaces (e.g., "Jarvan IV")
    let formattedChampionName = championName
      .replace(/'/g, '') // Remove apostrophes
      .replace(/\./g, ''); // Remove periods
    
    // Special cases - champions with specific capitalization patterns that must be preserved
    // These match Riot's Data Dragon API exact formatting
    const specialCases: Record<string, string> = {
      'kogmaw': 'KogMaw',
      'kog maw': 'KogMaw',
      'leblanc': 'Leblanc',
      'le blanc': 'Leblanc',
      'aurelionsol': 'AurelionSol',
      'aurelion sol': 'AurelionSol',
      'belveth': 'Belveth',
      'chogath': 'Chogath',
      'drmundo': 'DrMundo',
      'dr mundo': 'DrMundo',
      'dr. mundo': 'DrMundo',
      'jarvaniv': 'JarvanIV',
      'jarvan iv': 'JarvanIV',
      'khazix': 'Khazix',
      'reksai': 'RekSai',
      'tahmkench': 'TahmKench',
      'tahm kench': 'TahmKench',
      'velkoz': 'Velkoz',
      'missfortune': 'MissFortune',
      'miss fortune': 'MissFortune',
      'twistedfate': 'TwistedFate',
      'twisted fate': 'TwistedFate',
      'masteryi': 'MasterYi',
      'master yi': 'MasterYi',
      'xinzhao': 'XinZhao',
      'xin zhao': 'XinZhao',
      'leesin': 'LeeSin',
      'lee sin': 'LeeSin',
      'nunu': 'Nunu',
      'nunu & willump': 'Nunu',
      'wukong': 'MonkeyKing',
      'monkeyking': 'MonkeyKing',
      'renataglasc': 'Renata',
      'renata glasc': 'Renata',
      'renata': 'Renata',
      'ksante': 'KSante',
      'k\'sante': 'KSante',
    };
    
    const lowerCaseName = formattedChampionName.toLowerCase();
    if (specialCases[lowerCaseName]) {
      formattedChampionName = specialCases[lowerCaseName];
    } else if (formattedChampionName.includes(' ')) {
      // Check if the name contains a space - if so, format each word
      formattedChampionName = formattedChampionName
        .split(' ')
        .map(word => {
          // Keep Roman numerals uppercase (IV, etc.)
          if (/^[IVX]+$/i.test(word)) {
            return word.toUpperCase();
          }
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join('');
    } else {
      // If no space, check if it ends with a Roman numeral pattern and preserve it
      // Match patterns like "JarvanIV", "JarvanIv", "jarvaniv", etc.
      const romanNumeralMatch = formattedChampionName.match(/^(.+?)(IV)$/i);
      if (romanNumeralMatch) {
        const baseName = romanNumeralMatch[1];
        const romanNumeral = romanNumeralMatch[2];
        formattedChampionName = baseName.charAt(0).toUpperCase() + baseName.slice(1).toLowerCase() + romanNumeral.toUpperCase();
      } else {
        // Standard title case for single-word champions
        formattedChampionName = formattedChampionName.charAt(0).toUpperCase() + formattedChampionName.slice(1).toLowerCase();
      }
    }
    
    console.log(`[DataService] Original championName: "${championName}" -> Formatted: "${formattedChampionName}"`);
    const url = `${this.baseUrl}/${version}/data/en_US/champion/${formattedChampionName}.json`;
    console.log(`Fetching data from URL: ${url}`);
    
    const response = await fetch(url);
    if (!response.ok) {
      console.error(
        `Error fetching data from URL: ${url} - Status: ${response.status}`
      );
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const json = await response.json();
    console.log(`Fetched data for champion: ${formattedChampionName}`, json);
    
    const championData: ChampionHeaderData = json.data[formattedChampionName];
    
    championData.spells = championData.spells.map((spell) => ({
      ...spell,
      imagePath: `${this.baseUrl}/${version}/img/spell/${spell.image.full}`,
    }));
    
    championData.passive = {
      ...championData.passive,
      imagePath: `${this.baseUrl}/${version}/img/passive/${championData.passive.image.full}`,
    };
    
    return championData;
  }    

  getCustomSpellImages(championName: string): Record<string, string> {
    return customSpellImages[championName] || {};
  }

  public async fetchChampionItems(
    championName: string
  ): Promise<Record<string, string[]>> {
    const response = await fetch(
              `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/items/${championName}`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch champion items");
    }
    return response.json();
  }

  //Image URL functions

  // Instance method that delegates to static method
  async fetchLatestVersion(): Promise<string> {
    const version = await DataService.fetchLatestVersion();
    this.latestVersion = version;
    return version;
  }

  public async getChampionImageUrl(imageName: string): Promise<string> {
    const version = await this.fetchLatestVersion();
    //console.log("Image name:", imageName);
    return `${this.baseUrl}/${version}/img/champion/${imageName}`;
  }

  public async getPassiveImageUrl(imageName: string): Promise<string> {
    const version = await this.fetchLatestVersion();
    return `${this.baseUrl}/${version}/img/passive/${imageName}`;
  }

  public async getSpellImageUrl(imageName: string): Promise<string> {
    const version = await this.fetchLatestVersion();
    return `${this.baseUrl}/${version}/img/spell/${imageName}`;
  }

  public getRuneImagePath(runeName: string): string {
    // Special case for "Shield Bash"
    /*
    if (runeName === 'Shield Bash') {
      // Assuming shieldBashImage is imported or defined elsewhere in your DataService
      return shieldBashImage; 
    }
    */
    // Handle all other runes
    const pathSegment = runePathMappings[runeName] || "";
    return pathSegment.startsWith("http")
      ? pathSegment
      : `https://ddragon.leagueoflegends.com/cdn/img/perk-images/${pathSegment}`;
  }

  public fetchAllRuneImagePaths = (): Record<string, string> => {
    const runeImagePaths: Record<string, string> = {};

    for (const [runeName, pathSegment] of Object.entries(runePathMappings)) {
      runeImagePaths[runeName] = pathSegment.startsWith("http")
        ? pathSegment
        : `https://ddragon.leagueoflegends.com/cdn/img/perk-images/${pathSegment}`;
    }

    return runeImagePaths;
  };

  public fetchAllRuneTreeImages(): Record<string, string> {
    // Assuming you have an object that maps the rune tree keys to their image paths
    const runeTreeKeys = Object.keys(RUNE_TREES) as Array<
      keyof typeof RUNE_TREES
    >;
    const runeTreeImages: Record<string, string> = {};

    runeTreeKeys.forEach((treeKey) => {
      runeTreeImages[treeKey] = RUNE_TREES[treeKey].treeImage;
    });

    return runeTreeImages;
  }

  public fetchAllShardImagePaths(): ShardImagePaths {
    let shardImagePaths: ShardImagePaths = {};

    Object.keys(SHARD_ICON_PATHS).forEach((shardKey) => {
      const imagePath =
        SHARD_ICON_PATHS[shardKey as keyof typeof SHARD_ICON_PATHS];
      shardImagePaths[shardKey] = imagePath;
    });

    return shardImagePaths;
  }

  public async fetchAllItemImages(): Promise<Record<string, string>> {
    const items = await this.fetchAllItemsData();
    const itemImageUrls: Record<string, string> = {};

    for (const item of items) {
  //    console.log("Fetching image for item:", item);
      const imageUrl = await this.getItemImageUrl(item.image?.full || 'missing.png');
      itemImageUrls[item.id] = imageUrl;
    }

    return itemImageUrls;
  }

  public async getItemImageUrl(itemKey: string): Promise<string> {
    const version = await this.fetchLatestVersion();
    return `https://ddragon.leagueoflegends.com/cdn/${version}/img/item/${itemKey}`;
  }

  public async getItemImageUrlFromKey(itemKey: string): Promise<string> {
    const version = await this.fetchLatestVersion();
    return `https://ddragon.leagueoflegends.com/cdn/${version}/img/item/${itemKey}.png`;
  }

  public async fetchAllSummonerSpellImages(): Promise<Record<string, string>> {
    const version = await this.fetchLatestVersion();
    const images: Record<string, string> = {};

    for (const [spellName, spellKey] of Object.entries(summonerSpellNameToKey)) {
      const imageUrl = `https://ddragon.leagueoflegends.com/cdn/${version}/img/spell/${spellKey}.png`;
      images[spellName] = imageUrl;
    }

    return images;
  }

  public async getSummonerSpellImageUrl(spellName: string): Promise<string> {
    const version = await this.fetchLatestVersion();
    const spellKey =
      summonerSpellNameToKey[spellName as keyof typeof summonerSpellNameToKey];
    if (!spellKey) {
      throw new Error(`Summoner spell key not found for: ${spellName}`);
    }
    return `https://ddragon.leagueoflegends.com/cdn/${version}/img/spell/${spellKey}.png`;
  }
}

const dataService = new DataService();
export default dataService;
